/**
 * The ACTOR interceptor (D232) — a command that names a body is claimed
 * before the item roster sees it (`docs/design/actors.md` §2; Mike's ruling
 * 1, 2026-10-04: an actor is a new entity kind).
 *
 * @remarks
 * The fourth of the narrowing interceptors (D41 the room, D63 the hands, D56
 * the switch — see `narrow-intercepts.ts`), and the first to run, because
 * every arm of the roster below assumes a thing's reference is an item id
 * and would hand an actor to `requireItem`. One shape, three answers:
 *
 * - **Speech falls through.** `ask` — and `talk`, `greet`, `speak with`,
 *   which are its aliases — is the roster's `ask` arm, which reads an actor
 *   as one party of the question (addressee or subject) exactly as it reads
 *   a thing or the room (D231). The interceptor declines it.
 * - **Looking is mechanized.** `inspect` / `look at` aimed at an actor
 *   narrows to `inspect_actor`: the player seeing them — or perceiving them,
 *   through a door — in the environment's channel, through the describer
 *   items and rooms share.
 * - **Striking is mechanized on a FOE** (D234; Mike's ruling 3). `attack`
 *   — and `hit`, `strike`, `fight`, `kill`, its aliases — aimed at a body
 *   with a `combat` block narrows to `attack`, carrying the thing named
 *   after `with` for the handler's ladder; the loader has proved such a
 *   body's hostility can come true, so it is attackable whether or not it
 *   is hostile now. Aimed at a body with no block it refuses, in the
 *   actor's own words (`verbNarratives.attack`) or the `notAFoe` slot's —
 *   "She would notice" is the better content, and the pack writes it.
 * - **A thing passes to a body** (D235). `give` / `hand` / `offer` and
 *   `show`, with a body after `to`, narrow to `give` / `show` carrying the
 *   thing and the body: the handler reads the body's `giveInteractions`.
 *   Like speech, a gift passes where the hands do not — the occupant behind
 *   the steel door is offered the film and refuses it in her own words —
 *   so the branch sits BEFORE the reach gate. A body as the thing offered
 *   (`give the woman to …`) is a verb on a body, refused below; a thing
 *   after `to` is the roster's (`narrow-give-command.ts`); a room or the
 *   hands as the gift falls through to the intercepts that own them.
 * - **A body that carries things is gone through** (D240). `search` aimed
 *   at a body some thing declares itself carried by (`containedIn
 *   .containerActorId`), or that answers a search in its own words
 *   (`searchNarratives`), narrows to `search_actor`; its concealed pockets
 *   are found as a container's are. It is judged AFTER the reach gate: a
 *   body heard through steel cannot be patted down.
 * - **A thing is taken from a body, and used on one** (D242). `take <thing>
 *   from <body>` is the take when the body carries the thing
 *   (`narrow-take-from.ts`), judged after reach. `use <thing> on <body>` is a
 *   gift to a body that takes gifts and has no words of its own for `use`
 *   (the Meridian's §13.2: `use the cell on TALLY`), judged with the gifts —
 *   and, D256, a strike on a foe when the thing is a weapon or the foe takes
 *   no gifts (`use the cutter on the tender`), judged after reach.
 * - **A pack's own verb is the body's act when it has arms for it** (D251).
 *   `release tally` reads TALLY's `verbInteractions`, as `release the dock`
 *   reads the dock's; judged after reach, because an act is done with the
 *   hands. With no arm for the verb, the body refuses as below.
 * - **Everything else refuses.** An actor cannot be taken, opened, used,
 *   searched, worn, or struck with the pack's own verbs — not because a
 *   handler checked, but because none of those handlers can receive it. The
 *   refusal is the actor's own (`verbNarratives`, keyed by canonical verb,
 *   D47's shape; its readings chosen with the sentence in hand, D219) or the
 *   `verbOnActor` slot's. A verb aimed at an actor named in ANY slot refuses
 *   — `use the crowbar on the woman` is as much a verb on a body as `take
 *   the woman` — with the same precedence the room intercept reads.
 *
 * Reach comes before the hands (R29's rule for things, read for bodies): an
 * actor the player HEARS and cannot reach — one addressable through its
 * `audibleFrom`, Calder's occupant behind the steel door — refuses every verb
 * but speech, a gift and a look with its `unreachableNarratives` or the
 * `actorOutOfReach` slot. Looking is observation, and observation is exempt
 * from the barrier for a body as it is for a thing behind glass (R29): D232
 * refused the look ("a voice through steel is not a face"), and D235 let it
 * through, because the occupant's page is exactly what you perceive of her
 * — "You cannot see her. You can hear where she is" — and a description is
 * the place a pack says that. With no state to consult (tooling, tests)
 * reach is not judged, in the safe direction every other state-optional
 * narrowing takes: no refusal the world did not earn.
 *
 * @packageDocumentation
 */

import type { ActorDefinition, ActorId, ItemId, RuntimeState } from '@orpheus/content-schema';
import type { EntityRef, ParsedCommand } from '@orpheus/parser-engine';

import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';
import type { CommandNarrowingOutcome } from './runtime-action.js';

import { requireActor, requireItem } from '../bundle/bundle-lookups.js';
import { commandInHandOf } from '../conditions/command-in-hand.js';
import { speakSlot } from '../defaults/default-responses.js';
import { isActorAddressable, isActorPresent, isActorSearchable } from '../entities/actor-presence.js';
import { actorIdForEntityRef, isBodyRef, itemIdForEntityRef, roomIdForEntityRef } from '../entities/entity-ref.js';
import { resolveNarrativeLines } from '../narrative/conditional-text.js';
import { authoredActOf } from './authored-act.js';
import { refuseRoomAimedVerb } from './narrow-intercepts.js';
import { narrowed, rejected } from './narrow-parsed-command.js';
import { narrowTakeFrom } from './narrow-take-from.js';

/** The verbs that look at a thing — the one act an actor answers besides speech. */
const LOOKING_INTENT_TOKENS: readonly string[] = ['inspect', 'look'];

/** D240: the verb that goes through a thing — mechanized on a body that carries something. */
const SEARCH_INTENT_TOKEN = 'search';

/** The verb that speaks to a thing — never the interceptor's; the `ask` arm reads actors itself. */
const SPEECH_INTENT_TOKEN = 'ask';

/** The verb that strikes a body (D234) — mechanized on a foe, refused in D47's shape on a thing. */
export const ATTACK_INTENT_TOKEN = 'attack';

/** D235: the verbs that pass a thing to a body — `give` keeps or hands back by the arm's `consumesItem`; `show` always hands back. */
export const GIFT_INTENT_TOKENS: readonly ('give' | 'show')[] = ['give', 'show'];

/** D242: the verb a thing is taken with — `take <thing> from <body>` takes from the body's pockets (D240). */
const TAKE_INTENT_TOKEN = 'take';

/** D242 (§13.2): the verb that becomes a gift when it is aimed at a body that takes gifts. */
const USE_INTENT_TOKEN = 'use';

/**
 * Is this reference a THING — an item, not a body, a room or the player's hands?
 *
 * @param entityRef - A command slot's reference
 * @returns Whether it names an item
 */
const isThingRef = (entityRef: EntityRef): boolean =>
	actorIdForEntityRef(entityRef) === undefined && roomIdForEntityRef(entityRef) === undefined && !isBodyRef(entityRef);

/**
 * The thing in `use <thing> on <body>`, when that is the sentence — the thing
 * the direct object, this body after `on` or `with` — and the pack wrote no
 * words of its own for `use` on the body (`verbNarratives.use`, D47's shape),
 * which always answer it.
 *
 * @param command - The parsed proposal
 * @param intentText - The canonical verb text
 * @param actor - The body the command names
 * @returns The thing's item id, or `undefined` when the sentence is another
 */
const thingUsedOn = (command: ParsedCommand, intentText: string, actor: ActorDefinition): ItemId | undefined => {
	const { directObjectRef, indirectObjectRef, toolRef } = command;
	const isBodyTheTarget = [indirectObjectRef, toolRef].some((slot) => slot !== undefined && actorIdForEntityRef(slot) === actor.actorId);

	return intentText === USE_INTENT_TOKEN && directObjectRef !== undefined && isThingRef(directObjectRef) && isBodyTheTarget && actor.verbNarratives?.[USE_INTENT_TOKEN] === undefined
		? itemIdForEntityRef(directObjectRef)
		: undefined;
};

/** Does this body take things — a gift arm, or its own words for refusing every gift (D235)? */
const isGiftTaker = (actor: ActorDefinition): boolean => actor.giveInteractions.length > 0 || actor.refusesGiftsNarratives !== undefined;

/**
 * What `use <thing> on <body>` is, when it is something the body answers: a
 * strike (D256) or a gift (D242) — or `undefined`, and D232's refusal.
 *
 * @remarks
 * D242 (the Meridian's §13.2): players type `use the cell on TALLY` for what
 * `give the cell to TALLY` does, and a body that takes things has said what
 * taking one means. D256 (Mike, at MA-3): players type `use the cutter on
 * the tender` for what `attack the tender with the cutter` does, and a FOE
 * (a body with `combat`) has said what a thing swung at it means. A weapon
 * swung at a foe is the strike. Anything else offered to a body that takes
 * things is the gift; offered to a foe that takes nothing, it is the strike
 * too, and the strike's ladder says it is no weapon — closer to what the
 * player meant than being told the foe is not a thing you use.
 *
 * @param command - The parsed proposal
 * @param bundle - The loaded bundle
 * @param intentText - The canonical verb text
 * @param actor - The body the command names
 * @returns The reading, carrying the thing
 */
const useOnBodyAs = (
	command: ParsedCommand,
	bundle: EngineGameBundle,
	intentText: string,
	actor: ActorDefinition,
): { readonly itemId: ItemId; readonly type: 'give' | 'strike' } | undefined => {
	const itemId = thingUsedOn(command, intentText, actor);

	if (itemId === undefined) {
		return undefined;
	}

	if (actor.combat !== undefined && (requireItem(bundle, itemId).weapon !== undefined || !isGiftTaker(actor))) {
		return { itemId, type: 'strike' };
	}

	return isGiftTaker(actor) ? { itemId, type: 'give' } : undefined;
};

/** Is this canonical verb one of the two gift verbs? A type guard so the action it mints is typed by the verb. */
const isGiftIntent = (intentText: string): intentText is 'give' | 'show' => (GIFT_INTENT_TOKENS as readonly string[]).includes(intentText);

/**
 * Narrow a command that names an ACTOR in any of its slots (D232).
 *
 * @param command - The parsed proposal
 * @param bundle - The loaded bundle
 * @param intentText - The canonical verb text
 * @param state - The live runtime state, when the caller has one
 * @returns The narrowed outcome, or `undefined` when no actor is named or the verb is speech
 */
export const narrowActorAimedCommand = (
	command: ParsedCommand,
	bundle: EngineGameBundle,
	intentText: string,
	state: RuntimeState | undefined,
): CommandNarrowingOutcome | undefined => {
	const actorId = firstActorNamedBy(command);

	if (actorId === undefined || intentText === SPEECH_INTENT_TOKEN) {
		return undefined;
	}

	const actor = requireActor(bundle, actorId);

	// D235: a thing offered to the body — speech's sibling, judged before
	// reach, because a gift is offered across the same steel a word crosses.
	// D242 (§13.2): `use the cell on TALLY` is that gift, to a body that takes them.
	const gift = command.directObjectRef;
	const usedAs = useOnBodyAs(command, bundle, intentText, actor);

	if (usedAs?.type === 'give') {
		return narrowed({ actorId, itemId: usedAs.itemId, type: 'give' });
	}

	if (isGiftIntent(intentText) && (gift === undefined || actorIdForEntityRef(gift) === undefined)) {
		if (gift === undefined || roomIdForEntityRef(gift) !== undefined || isBodyRef(gift)) {
			// Nothing to offer, or a room or the hands as the gift: not this
			// intercept's — the roster asks "what?", the room and the body
			// intercepts refuse in their own sentences.
			return undefined;
		}

		return narrowed({ actorId, itemId: itemIdForEntityRef(gift), type: intentText });
	}

	const isAimedAt = command.directObjectRef !== undefined && actorIdForEntityRef(command.directObjectRef) === actorId;

	// Looking is observation — exempt from the barrier (R29; D235 for bodies):
	// what the player perceives of someone they cannot reach is the page's to say.
	if (LOOKING_INTENT_TOKENS.includes(intentText) && isAimedAt) {
		return narrowed({ actorId, type: 'inspect_actor' });
	}

	// Reach before the hands: a body heard through a door is not one the hands
	// reach. Speech never gets here. A body that is not even addressable (a
	// which-one answer about someone the player walked away from) is not
	// judged for reach — it narrows, and step 2's catalog check refuses it as
	// not here, exactly as a stale item is refused.
	if (state !== undefined && isActorAddressable(bundle, state, actorId) && !isActorPresent(bundle, state, actorId)) {
		return rejected(
			bundle,
			state,
			{ actorId, type: 'actor_out_of_reach' },
			actor.unreachableNarratives ?? [speakSlot(bundle, state, 'actorOutOfReach', { itemName: actor.name })],
		);
	}

	return narrowHandsOnBody(command, bundle, intentText, state, actor, usedAs?.type === 'strike' ? usedAs.itemId : undefined);
};

/**
 * What the hands do to a body, past reach: D234's strike (and D256's, a thing
 * used on a foe), D242's take from it, D251's authored act on it, D240's
 * search of it — or the refusal every other verb on a body earns.
 *
 * @remarks
 * The pack's own verb, aimed at a body with arms for it, is the body's act,
 * read as a thing's is (D251). A body that carries something, or answers a search in its own words, is
 * gone through when it is what the search is aimed at — `search tally`,
 * `search through tally` (the vocabulary reads `through` as no slot at all).
 * Any other body, a body named in another slot, and any other verb, refuse
 * ({@link refuseVerbOnActor}) — the strike's rule for which body a verb is
 * aimed at.
 *
 * @param command - The parsed proposal
 * @param bundle - The loaded bundle
 * @param intentText - The canonical verb text
 * @param state - The live runtime state, when the caller has one
 * @param actor - The body the command names
 * @param swungItemId - D256: the thing `use <thing> on <foe>` swings, when the sentence is that strike
 * @returns The strike, the take, the act, the search, or the typed refusal
 */
const narrowHandsOnBody = (
	command: ParsedCommand,
	bundle: EngineGameBundle,
	intentText: string,
	state: RuntimeState | undefined,
	actor: ActorDefinition,
	swungItemId: ItemId | undefined,
): CommandNarrowingOutcome => {
	const aimedRef = command.directObjectRef;
	const isAimedAtThisBody = aimedRef !== undefined && actorIdForEntityRef(aimedRef) === actor.actorId;

	if (intentText === ATTACK_INTENT_TOKEN && isAimedAtThisBody) {
		return narrowStrike(command, bundle, state, actor);
	}

	if (swungItemId !== undefined) {
		return narrowed({ actorId: actor.actorId, type: 'attack', weaponItemId: swungItemId });
	}

	// D242: `take the key from the guard` — a thing taken from the body named, judged after reach like any take.
	const takenRef = command.directObjectRef;

	if (intentText === TAKE_INTENT_TOKEN && command.indirectObjectRef !== undefined && takenRef !== undefined && isThingRef(takenRef)) {
		return narrowTakeFrom(bundle, state, itemIdForEntityRef(takenRef), command.indirectObjectRef);
	}

	if (isAimedAtThisBody && actor.verbInteractions?.[intentText] !== undefined) {
		return narrowed(authoredActOf(command, intentText, { actorId: actor.actorId }));
	}

	const isSearchOfThisBody = intentText === SEARCH_INTENT_TOKEN && isAimedAtThisBody;

	return isSearchOfThisBody && isActorSearchable(bundle, actor)
		? narrowed({ actorId: actor.actorId, type: 'search_actor' })
		: refuseVerbOnActor(command, bundle, intentText, state, actor);
};

/**
 * D234: `attack <actor> [with <item>]` — the strike, when the body is a foe;
 * the refusal in its own words when it is not.
 *
 * @remarks
 * The weapon is the TOOL slot (`with`, `using` — the bindings `attack`
 * declares). A thing in the `with` slot that is not an item (a second body,
 * the room) is not a weapon and refuses as any verb aimed at a body or a
 * room does — the intercepts before this one have already claimed those.
 *
 * @param command - The parsed proposal
 * @param bundle - The loaded bundle
 * @param state - The live runtime state, when the caller has one
 * @param actor - The body struck at
 * @returns The strike, or the typed refusal
 */
const narrowStrike = (
	command: ParsedCommand,
	bundle: EngineGameBundle,
	state: RuntimeState | undefined,
	actor: ActorDefinition,
): CommandNarrowingOutcome => {
	if (actor.combat === undefined) {
		const authoredLines = actor.verbNarratives?.[ATTACK_INTENT_TOKEN];

		return rejected(
			bundle,
			state,
			{ actorId: actor.actorId, type: 'not_a_foe' },
			authoredLines === undefined
				? [speakSlot(bundle, state, 'notAFoe', { intent: command.intentSurface.toUpperCase(), itemName: actor.name, verb: command.intentSurface.toLowerCase() })]
				: resolveNarrativeLines(authoredLines, bundle, state, commandInHandOf(command)),
		);
	}

	if (command.toolRef === undefined) {
		return narrowed({ actorId: actor.actorId, type: 'attack' });
	}

	// The thing after `with` is a room, or another body: nothing a hand swings.
	if (roomIdForEntityRef(command.toolRef) !== undefined) {
		return refuseRoomAimedVerb(command, bundle, ATTACK_INTENT_TOKEN, state);
	}

	const wieldedActorId = actorIdForEntityRef(command.toolRef);

	if (wieldedActorId !== undefined) {
		return refuseVerbOnActor(command, bundle, ATTACK_INTENT_TOKEN, state, requireActor(bundle, wieldedActorId));
	}

	return narrowed({ actorId: actor.actorId, type: 'attack', weaponItemId: itemIdForEntityRef(command.toolRef) });
};

/**
 * Refuse a verb aimed at an actor: in the actor's own words for this verb
 * when it has them, else the `verbOnActor` slot's.
 *
 * @param command - The parsed proposal (its surface verb is quoted, D36; its slots choose a line's reading, D219)
 * @param bundle - The loaded bundle
 * @param intentText - The canonical verb text — the key the actor's `verbNarratives` are read with
 * @param state - The live runtime state, when the caller has one
 * @param actor - The actor the verb was aimed at
 * @returns The typed refusal
 */
const refuseVerbOnActor = (
	command: ParsedCommand,
	bundle: EngineGameBundle,
	intentText: string,
	state: RuntimeState | undefined,
	actor: ActorDefinition,
): CommandNarrowingOutcome => {
	const authoredLines = actor.verbNarratives?.[intentText];
	const answeredLines = authoredLines === undefined ? undefined : resolveNarrativeLines(authoredLines, bundle, state, commandInHandOf(command));

	return rejected(
		bundle,
		state,
		{ actorId: actor.actorId, type: 'verb_on_actor', verbToken: intentText },
		answeredLines ?? [
			speakSlot(bundle, state, 'verbOnActor', {
				intent: command.intentSurface.toUpperCase(),
				itemName: actor.name,
				verb: command.intentSurface.toLowerCase(),
			}),
		],
	);
};

/**
 * The first actor a command names, in the precedence every intercept reads:
 * the direct object, then the bound phrase, then the tool slot.
 *
 * @param command - The parsed proposal
 * @returns The actor's id, or `undefined` when no slot names one
 */
const firstActorNamedBy = (command: ParsedCommand): ActorId | undefined => {
	const slots: readonly (EntityRef | undefined)[] = [command.directObjectRef, command.indirectObjectRef, command.toolRef];

	for (const slot of slots) {
		const actorId = slot === undefined ? undefined : actorIdForEntityRef(slot);

		if (actorId !== undefined) {
			return actorId;
		}
	}

	return undefined;
};
